import type { ConeDay, ReadingPoint, TankChange } from "./api";
import { STALE_AFTER_MS } from "./format";
import { personName } from "./requests";

export const RANGES = [7, 30, 90] as const;
export type Range = (typeof RANGES)[number];

// A span with `full: null` is a time with no readings (rule S-6).
export interface Span {
  start: number;
  end: number;
  full: boolean | null;
}

export interface Tick {
  x: number;
  label: string;
}

const time = (iso: string) => new Date(iso).getTime();

/** A function from a time to an x position between `left` and `right`. */
export function xScale(from: string, to: string, left: number, right: number): (iso: string) => number {
  const start = time(from);
  const width = Math.max(1, time(to) - start);
  return (iso) => left + ((time(iso) - start) / width) * (right - left);
}

const iso = (ms: number) => new Date(ms).toISOString();

/** Each reading lasts until the next one or `to`, but never longer than `staleMs`; then a gap. Equal neighbours join. */
export function levelSpans(
  points: readonly ReadingPoint[],
  to: string,
  x: (iso: string) => number,
  staleMs: number = STALE_AFTER_MS,
  from?: string,
): Span[] {
  const spans: Span[] = [];
  const floor = from === undefined ? -Infinity : time(from);
  const add = (startMs: number, endMs: number, full: boolean | null) => {
    const begin = Math.max(startMs, floor);
    if (endMs < begin) return;
    const start = x(iso(begin));
    const end = x(iso(endMs));
    const last = spans[spans.length - 1];
    if (last && last.full === full && Math.abs(last.end - start) < 0.01) last.end = end;
    else spans.push({ start, end, full });
  };
  const first = time(points.length ? points[0].at : to);
  if (from !== undefined && first > floor) add(floor, first, null);
  points.forEach((point, index) => {
    if (point.full === null) return;
    const next = time(index + 1 < points.length ? points[index + 1].at : to);
    const stop = Math.min(next, time(point.at) + staleMs);
    add(time(point.at), stop, point.full);
    if (stop < next) add(stop, next, null);
  });
  return spans;
}

/** The reading before the range when it has a level, then the readings of the range (rule S-6). */
export function withStart(points: readonly ReadingPoint[], before: ReadingPoint | null | undefined): ReadingPoint[] {
  return before && before.full !== null ? [before, ...points] : [...points];
}

/** An SVG path of the battery percent. A reading with no battery value breaks the line. */
export function batteryPath(
  points: readonly ReadingPoint[],
  x: (iso: string) => number,
  top: number,
  bottom: number,
): string {
  let path = "";
  let drawing = false;
  for (const point of points) {
    if (point.battery_pct === null) {
      drawing = false;
      continue;
    }
    const pct = Math.min(100, Math.max(0, point.battery_pct));
    const y = bottom - (pct / 100) * (bottom - top);
    path += `${drawing ? "L" : "M"}${x(point.at).toFixed(1)} ${y.toFixed(1)}`;
    drawing = true;
  }
  return path;
}

const DAY_MS = 86_400_000;

/** How far the clock of the time zone is ahead of UTC at this time, in ms. */
function zoneOffset(ms: number, timeZone?: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(new Date(ms));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const local = Date.UTC(part("year"), part("month") - 1, part("day"), part("hour"), part("minute"), part("second"));
  return local - Math.floor(ms / 1000) * 1000;
}

/** The days between two date labels: each day on a 7-day chart, fewer on a phone or a long range. */
export function tickEvery(days: number, narrow: boolean): number {
  if (days <= 7) return narrow ? 2 : 1;
  if (days <= 30) return 7;
  return narrow ? 21 : 14;
}

/** A date label at each `every`-th midnight of the time zone in the range; the newest midnight always has one. */
export function dayTicks(from: string, to: string, every: number, x: (iso: string) => number, timeZone?: string): Tick[] {
  const start = time(from);
  const end = time(to);
  const local = new Date(start + zoneOffset(start, timeZone));
  const format = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone });
  const midnights: number[] = [];
  for (let day = 0; day * DAY_MS <= end - start + DAY_MS; day += 1) {
    const guess = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + day);
    const midnight = guess - zoneOffset(guess - zoneOffset(guess, timeZone), timeZone);
    if (midnight >= start && midnight <= end) midnights.push(midnight);
  }
  return midnights
    .filter((_, index) => (midnights.length - 1 - index) % every === 0)
    .map((ms) => ({ x: x(iso(ms)), label: format.format(new Date(ms)) }));
}

/** The text anchor of a date label, so a label near an edge stays inside the chart. */
export function tickAnchor(at: number, left: number, right: number, room = 24): "start" | "middle" | "end" {
  if (at - left < room) return "start";
  if (right - at < room) return "end";
  return "middle";
}

const length = (spans: readonly Span[]) => spans.reduce((sum, span) => sum + span.end - span.start, 0);

/** The part of the time with a level that the tank was full. A gap with no readings does not count. */
export function fullShare(spans: readonly Span[]): number {
  const total = length(spans.filter((span) => span.full !== null));
  if (!total) return 0;
  return length(spans.filter((span) => span.full === true)) / total;
}

/** The part of the whole width with no readings, from 0 to 1. */
export function gapShare(spans: readonly Span[], width: number): number {
  return width > 0 ? length(spans.filter((span) => span.full === null)) / width : 0;
}

const ACTORS: Record<string, string> = { user: "A member", system: "The system", service: "An admin job", api_key: "An API key" };

/** One line for a change of the tank in its history list. */
export function changeText(change: TankChange): string {
  const after = change.after ?? {};
  const before = change.before ?? {};
  if (change.action === "tank.create") {
    const device = after.device as { source?: string; external_id?: string } | undefined;
    return device ? `Added by the first report of ${device.source} ${device.external_id}` : "Added";
  }
  if (change.action === "tank.rename") {
    const name = `Named "${String(after.label ?? "")}"`;
    return before.label ? `${name} (was "${String(before.label)}")` : name;
  }
  if (change.action === "tank.assign") return "A driver was assigned";
  if (change.action === "tank.use") return after.in_field ? "Marked as a container in the field" : 'Marked "Test"';
  return change.action;
}

export const actorText = (actor: string) => ACTORS[actor] ?? personName(actor);

export interface ConePaths {
  outer: string;
  inner: string;
  median: string;
  /** The days where the median goes over the top of the chart. */
  over: number[];
}

export interface SharePoint {
  day: number;
  share: number;
}

/** The parts of a line at or under `top`, cut where it crosses the top; `exits` are the days where it goes over. */
export function clipTop(points: readonly SharePoint[], top: number): { runs: SharePoint[][]; exits: number[] } {
  const runs: SharePoint[][] = [];
  const exits: number[] = [];
  let run: SharePoint[] = [];
  points.forEach((point, index) => {
    const before = points[index - 1];
    const cross = () => ({ day: before.day + ((top - before.share) / (point.share - before.share)) * (point.day - before.day), share: top });
    if (point.share <= top) {
      if (before && before.share > top) run = [cross()];
      run.push(point);
      return;
    }
    if (!before) exits.push(point.day);
    else if (before.share <= top) {
      const edge = cross();
      run.push(edge);
      exits.push(edge.day);
    }
    if (run.length) runs.push(run);
    run = [];
  });
  if (run.length) runs.push(run);
  return { runs, exits };
}

type ConeKey = "p2" | "p16" | "p50" | "p84" | "p97";

/** The runs of days that have all five quantiles; a day with a null breaks the run. */
function coneRuns(days: readonly ConeDay[]): ConeDay[][] {
  const runs: ConeDay[][] = [];
  let run: ConeDay[] = [];
  for (const day of days) {
    if ([day.p2, day.p16, day.p50, day.p84, day.p97].some((value) => value === null)) {
      if (run.length) runs.push(run);
      run = [];
    } else run.push(day);
  }
  if (run.length) runs.push(run);
  return runs;
}

/** The SVG paths of the cone (rule U-24): the 95% band (p2.5 to p97.5), the 68% band (p16 to p84) and the median. */
export function conePaths(days: readonly ConeDay[], x: (day: number) => number, y: (share: number) => number, top = Infinity): ConePaths {
  const point = (day: ConeDay, key: ConeKey) => `${x(day.day).toFixed(1)} ${y(day[key] as number).toFixed(1)}`;
  const line = (run: ConeDay[], key: ConeKey) => run.map((day, index) => `${index ? "L" : "M"}${point(day, key)}`).join("");
  const area = (run: ConeDay[], top: ConeKey, bottom: ConeKey) =>
    `${line(run, top)}${[...run].reverse().map((day) => `L${point(day, bottom)}`).join("")}Z`;
  const runs = coneRuns(days);
  const median = runs.map((run) => clipTop(run.map((day) => ({ day: day.day, share: day.p50 as number })), top));
  return {
    outer: runs.map((run) => area(run, "p97", "p2")).join(""),
    inner: runs.map((run) => area(run, "p84", "p16")).join(""),
    median: median.flatMap((part) => part.runs.map((points) => conePointsPath(points, x, y))).join(""),
    over: median.flatMap((part) => part.exits),
  };
}

/** The days from `nowMs` to a time; a date with no time counts from its UTC midnight. */
export const daysFrom = (iso: string, nowMs: number) => (Date.parse(iso) - nowMs) / DAY_MS;

/** The thin line through the level readings of the cone history, in time order. */
export function conePointsPath(points: readonly SharePoint[], x: (day: number) => number, y: (share: number) => number): string {
  return points.map((point, index) => `${index ? "L" : "M"}${x(point.day).toFixed(1)} ${y(point.share).toFixed(1)}`).join("");
}

/** A local calendar date on the cone axis: `start` is the day offset from now of its local midnight. */
export interface CalendarDay {
  offset: number;
  start: number;
  date: string;
  weekday: number;
}

const dateOf = (dayNumber: number) => new Date(dayNumber * DAY_MS);

/** The local calendar dates from day `first` to day `last`; day d falls on the date of today + d calendar days. */
export function calendarDays(first: number, last: number, nowMs: number, timeZone?: string): CalendarDay[] {
  const local = nowMs + zoneOffset(nowMs, timeZone);
  const today = Math.floor(local / DAY_MS);
  const elapsed = (local - today * DAY_MS) / DAY_MS;
  const days: CalendarDay[] = [];
  for (let offset = Math.floor(first + elapsed); offset <= Math.floor(last + elapsed); offset += 1) {
    const date = dateOf(today + offset);
    days.push({ offset, start: offset - elapsed, date: date.toISOString().slice(0, 10), weekday: date.getUTCDay() });
  }
  return days;
}

export const isWeekend = (day: CalendarDay) => day.weekday === 0 || day.weekday === 6;

/** Each `every`-th Monday; the Monday of the next 7 days always has a label. */
export const labelMondays = (days: readonly CalendarDay[], every: number) =>
  days.filter((day) => day.weekday === 1 && ((Math.floor(day.offset / 7) % every) + every) % every === 0);

/** The day offset from now of the local midnight that starts a date (YYYY-MM-DD); a time keeps its exact offset. */
export function dateStart(date: string, nowMs: number, timeZone?: string): number {
  if (date.length > 10) return daysFrom(date, nowMs);
  const local = nowMs + zoneOffset(nowMs, timeZone);
  const today = Math.floor(local / DAY_MS);
  return Date.parse(date) / DAY_MS - today - (local - today * DAY_MS) / DAY_MS;
}

/** The text anchor of a label next to a line at `at`: after it, else before it, else null when neither fits. */
export function labelSide(at: number, width: number, left: number, right: number): "start" | "end" | null {
  if (at + 4 + width <= right) return "start";
  if (at - 4 - width >= left) return "end";
  return null;
}
